// combatpro/runtime/combatRuntime.js
//
// Combat PRO - per-combatant live runtime state (DM Operations Expansion).
//
// A single namespaced runtime object (`rt`) attached to each combatant (monster or
// player). It holds the live, ENCOUNTER-INSTANCE state that must not pollute the
// reusable canonical Monster Studio record: action economy, combat outcome state,
// Legendary Resistance uses, limited-use / recharge resources, death-save state,
// temp HP, tactical cover / visibility / elevation, current Battlefield Zone, and
// Side / Combat Group membership. Two identical monsters keep independent `rt`.
//
// Pure and UI-independent: data in, data out. It owns no React state and performs
// no side effects. Everything defaults, so a combatant with no `rt` behaves exactly
// as before (backward compatible), and `rt` rides the existing captureCombat path
// (monsters are cloned wholesale; players are captured under `players`). No optional
// chaining, no nullish coalescing, no em dash.

// ---- Vocabularies ----------------------------------------------------------
export var OUTCOMES = ['active', 'unconscious', 'stable', 'defeated', 'dead', 'fled', 'surrendered', 'captured', 'removed'];
export var OUTCOME_LABELS = {
  active: 'Active', unconscious: 'Unconscious', stable: 'Stable', defeated: 'Defeated',
  dead: 'Dead', fled: 'Fled / Escaped', surrendered: 'Surrendered', captured: 'Captured', removed: 'Removed'
};
// Outcomes that take a combatant out of the active initiative flow.
export var OUT_OF_FIGHT = ['defeated', 'dead', 'fled', 'surrendered', 'captured', 'removed'];

export var SIDES = ['party', 'enemy', 'ally', 'neutral', 'summoned', 'civilian'];
export var SIDE_LABELS = { party: 'Party', enemy: 'Enemy', ally: 'Ally', neutral: 'Neutral', summoned: 'Summoned', civilian: 'Civilian' };

export var COVERS = ['none', 'half', 'threequarters'];
export var COVER_LABELS = { none: 'No Cover', half: 'Half Cover', threequarters: 'Three-Quarters Cover' };
export var VISIBILITIES = ['clear', 'obscured', 'hidden', 'exposed'];
export var VISIBILITY_LABELS = { clear: 'Clear', obscured: 'Heavily Obscured', hidden: 'Hidden', exposed: 'Exposed' };

export var READY_STATES = ['none', 'ready', 'triggered', 'used', 'expired', 'cancelled'];
export var READY_STATE_LABELS = { none: 'None', ready: 'Ready', triggered: 'Triggered', used: 'Used', expired: 'Expired', cancelled: 'Cancelled' };
export var RESOURCE_KINDS = ['recharge', 'perday', 'uses', 'charges', 'pool'];
export var RESOURCE_KIND_LABELS = { recharge: 'Recharge', perday: 'X / Day', uses: 'Fixed Uses', charges: 'Charges', pool: 'Manual Pool' };

// ---- Ids -------------------------------------------------------------------
export function newRtId(prefix) {
  var p = prefix ? prefix : 'rt';
  return p + '_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 7);
}

// ---- Default rt ------------------------------------------------------------
export function defaultRt() {
  return {
    outcome: 'active',
    action: true, bonus: true, reaction: true,
    moveMax: 30, moveUsed: 0,
    legendaryResistance: { max: 0, used: 0 },
    resources: [],
    deathSaves: { successes: 0, failures: 0 },
    tempHP: 0,
    cover: 'none', visibility: 'clear', elevated: false,
    zone: null, side: null, group: null,
    wave: null, entered: true,
    ready: { action: '', trigger: '', reaction: true, status: 'none' }
  };
}

function isObj(x) { return x && typeof x === 'object'; }
function bool(x, d) { return (typeof x === 'boolean') ? x : d; }
function intOr(x, d) { var n = Number(x); return isNaN(n) ? d : Math.trunc(n); }
function clampInt(x, lo, hi, d) { var n = intOr(x, d); if (n < lo) { n = lo; } if (typeof hi === 'number' && n > hi) { n = hi; } return n; }

// Coerce any stored rt into a valid shape (used on read, so legacy combatants and
// partial data upgrade cleanly). Never throws.
export function normalizeRt(rt) {
  var d = defaultRt();
  if (!isObj(rt)) { return d; }
  d.outcome = (OUTCOMES.indexOf(rt.outcome) !== -1) ? rt.outcome : 'active';
  d.action = bool(rt.action, true);
  d.bonus = bool(rt.bonus, true);
  d.reaction = bool(rt.reaction, true);
  d.moveMax = clampInt(rt.moveMax, 0, null, 30);
  d.moveUsed = clampInt(rt.moveUsed, 0, null, 0);
  var lr = isObj(rt.legendaryResistance) ? rt.legendaryResistance : {};
  d.legendaryResistance = { max: clampInt(lr.max, 0, null, 0), used: clampInt(lr.used, 0, null, 0) };
  d.resources = Array.isArray(rt.resources) ? rt.resources.filter(isObj).map(normalizeResource) : [];
  var ds = isObj(rt.deathSaves) ? rt.deathSaves : {};
  d.deathSaves = { successes: clampInt(ds.successes, 0, 3, 0), failures: clampInt(ds.failures, 0, 3, 0) };
  d.tempHP = clampInt(rt.tempHP, 0, null, 0);
  d.cover = (COVERS.indexOf(rt.cover) !== -1) ? rt.cover : 'none';
  d.visibility = (VISIBILITIES.indexOf(rt.visibility) !== -1) ? rt.visibility : 'clear';
  d.elevated = bool(rt.elevated, false);
  d.zone = (typeof rt.zone === 'string' && rt.zone) ? rt.zone : null;
  d.side = (SIDES.indexOf(rt.side) !== -1) ? rt.side : null;
  d.group = (typeof rt.group === 'string' && rt.group) ? rt.group : null;
  d.wave = (typeof rt.wave === 'number' && rt.wave >= 1) ? Math.floor(rt.wave) : null;
  d.entered = bool(rt.entered, true);
  var rd = isObj(rt.ready) ? rt.ready : {};
  d.ready = {
    action: (typeof rd.action === 'string') ? rd.action : '',
    trigger: (typeof rd.trigger === 'string') ? rd.trigger : '',
    reaction: bool(rd.reaction, true),
    status: (READY_STATES.indexOf(rd.status) !== -1) ? rd.status : 'none'
  };
  return d;
}

function normalizeResource(r) {
  var kind = (RESOURCE_KINDS.indexOf(r.kind) !== -1) ? r.kind : 'uses';
  return {
    id: (typeof r.id === 'string' && r.id) ? r.id : newRtId('res'),
    name: (typeof r.name === 'string') ? r.name : '',
    kind: kind,
    max: clampInt(r.max, 0, null, 1),
    current: clampInt(r.current, 0, null, 1),
    rechargeMin: clampInt(r.rechargeMin, 1, 6, 6),
    rechargeMax: clampInt(r.rechargeMax, 1, 6, 6),
    note: (typeof r.note === 'string') ? r.note : ''
  };
}

// Read a combatant's rt (normalized). Non-mutating.
export function getRt(combatant) {
  return normalizeRt(combatant ? combatant.rt : null);
}

// Return a NEW combatant object with rt replaced by fn(currentRt). Immutable.
export function withRt(combatant, fn) {
  var rt = getRt(combatant);
  var next = fn(rt);
  return Object.assign({}, combatant, { rt: next ? next : rt });
}

// ---- Action economy --------------------------------------------------------
// Reset a combatant's action economy for the start of its turn (action, bonus,
// movement). Reaction is NOT reset here: it refreshes at the start of the combatant's
// turn in 5e, so it is reset together with the turn. We reset all three plus movement.
export function resetTurn(rt) {
  var d = Object.assign({}, rt);
  d.action = true; d.bonus = true; d.reaction = true; d.moveUsed = 0;
  return d;
}
export function setEconomy(rt, patch) {
  var d = Object.assign({}, rt);
  if (patch && typeof patch.action === 'boolean') { d.action = patch.action; }
  if (patch && typeof patch.bonus === 'boolean') { d.bonus = patch.bonus; }
  if (patch && typeof patch.reaction === 'boolean') { d.reaction = patch.reaction; }
  if (patch && typeof patch.moveUsed !== 'undefined') { d.moveUsed = clampInt(patch.moveUsed, 0, null, 0); }
  if (patch && typeof patch.moveMax !== 'undefined') { d.moveMax = clampInt(patch.moveMax, 0, null, 30); }
  return d;
}

// ---- Outcome ---------------------------------------------------------------
export function setOutcome(rt, outcome) {
  var o = (OUTCOMES.indexOf(outcome) !== -1) ? outcome : rt.outcome;
  return Object.assign({}, rt, { outcome: o });
}
export function isOutOfFight(rt) { return OUT_OF_FIGHT.indexOf(rt.outcome) !== -1; }

// ---- Legendary Resistance --------------------------------------------------
export function setLegendaryResistance(rt, patch) {
  var lr = Object.assign({}, rt.legendaryResistance);
  if (patch && typeof patch.max !== 'undefined') { lr.max = clampInt(patch.max, 0, null, 0); }
  if (patch && typeof patch.used !== 'undefined') { lr.used = clampInt(patch.used, 0, null, 0); }
  if (lr.used > lr.max) { lr.used = lr.max; }
  return Object.assign({}, rt, { legendaryResistance: lr });
}
export function spendLegendaryResistance(rt) {
  var lr = rt.legendaryResistance;
  if (!lr || lr.max <= 0 || lr.used >= lr.max) { return rt; }
  return Object.assign({}, rt, { legendaryResistance: { max: lr.max, used: lr.used + 1 } });
}
export function restoreLegendaryResistance(rt) {
  var lr = rt.legendaryResistance;
  if (!lr || lr.used <= 0) { return rt; }
  return Object.assign({}, rt, { legendaryResistance: { max: lr.max, used: lr.used - 1 } });
}
export function legendaryResistanceRemaining(rt) {
  var lr = rt.legendaryResistance;
  return lr ? Math.max(0, lr.max - lr.used) : 0;
}

// ---- Resources (recharge / per-day / uses / charges / pool) ----------------
export function addResource(rt, resource) {
  var r = normalizeResource(resource || {});
  return Object.assign({}, rt, { resources: (rt.resources || []).concat([r]) });
}
export function updateResource(rt, id, patch) {
  return Object.assign({}, rt, {
    resources: (rt.resources || []).map(function (r) {
      if (r.id !== id) { return r; }
      var m = normalizeResource(Object.assign({}, r, patch || {}));
      m.id = r.id;
      return m;
    })
  });
}
export function removeResource(rt, id) {
  return Object.assign({}, rt, { resources: (rt.resources || []).filter(function (r) { return r.id !== id; }) });
}
export function spendResource(rt, id) {
  return Object.assign({}, rt, {
    resources: (rt.resources || []).map(function (r) {
      if (r.id !== id) { return r; }
      return Object.assign({}, r, { current: Math.max(0, r.current - 1) });
    })
  });
}
export function restoreResource(rt, id) {
  return Object.assign({}, rt, {
    resources: (rt.resources || []).map(function (r) {
      if (r.id !== id) { return r; }
      return Object.assign({}, r, { current: Math.min(r.max, r.current + 1) });
    })
  });
}
// Apply a recharge roll (1-6) to a recharge resource: recharged when roll is within
// [rechargeMin, rechargeMax]. Returns { rt, recharged }.
export function applyRechargeRoll(rt, id, roll) {
  var recharged = false;
  var resources = (rt.resources || []).map(function (r) {
    if (r.id !== id || r.kind !== 'recharge') { return r; }
    if (roll >= r.rechargeMin && roll <= r.rechargeMax) { recharged = true; return Object.assign({}, r, { current: r.max }); }
    return r;
  });
  return { rt: Object.assign({}, rt, { resources: resources }), recharged: recharged };
}

// ---- Death saves (players; also usable for monsters) -----------------------
export function recordDeathSave(rt, kind) {
  var ds = Object.assign({}, rt.deathSaves);
  if (kind === 'success') { ds.successes = clampInt(ds.successes + 1, 0, 3, 0); }
  else if (kind === 'failure') { ds.failures = clampInt(ds.failures + 1, 0, 3, 0); }
  var out = rt.outcome;
  if (ds.successes >= 3) { out = 'stable'; }
  if (ds.failures >= 3) { out = 'dead'; }
  return Object.assign({}, rt, { deathSaves: ds, outcome: out });
}
export function resetDeathSaves(rt) {
  return Object.assign({}, rt, { deathSaves: { successes: 0, failures: 0 } });
}
export function setTempHP(rt, value) {
  return Object.assign({}, rt, { tempHP: clampInt(value, 0, null, 0) });
}

// ---- Tactical state --------------------------------------------------------
export function setTactical(rt, patch) {
  var d = Object.assign({}, rt);
  if (patch && COVERS.indexOf(patch.cover) !== -1) { d.cover = patch.cover; }
  if (patch && VISIBILITIES.indexOf(patch.visibility) !== -1) { d.visibility = patch.visibility; }
  if (patch && typeof patch.elevated === 'boolean') { d.elevated = patch.elevated; }
  if (patch && typeof patch.zone !== 'undefined') { d.zone = (typeof patch.zone === 'string' && patch.zone) ? patch.zone : null; }
  return d;
}
export function setSide(rt, side) {
  return Object.assign({}, rt, { side: (SIDES.indexOf(side) !== -1) ? side : null });
}
export function setGroup(rt, groupId) {
  return Object.assign({}, rt, { group: (typeof groupId === 'string' && groupId) ? groupId : null });
}
// Wave membership + entry state (Random & Wave: automatic sequential progression).
export function setWave(rt, n) {
  var w = parseInt(n, 10);
  return Object.assign({}, rt, { wave: (!isNaN(w) && w >= 1) ? w : null });
}
export function setEntered(rt, entered) {
  return Object.assign({}, rt, { entered: !!entered });
}


// ---- Ready / Held / Delayed actions ----------------------------------------
export function setReady(rt, patch) {
  var d = Object.assign({}, rt.ready);
  if (patch && typeof patch.action === 'string') { d.action = patch.action; }
  if (patch && typeof patch.trigger === 'string') { d.trigger = patch.trigger; }
  if (patch && typeof patch.reaction === 'boolean') { d.reaction = patch.reaction; }
  if (patch && READY_STATES.indexOf(patch.status) !== -1) { d.status = patch.status; }
  return Object.assign({}, rt, { ready: d });
}
export function clearReady(rt) {
  return Object.assign({}, rt, { ready: { action: '', trigger: '', reaction: true, status: 'none' } });
}
